import { useLayoutEffect } from 'react';
import './portraitBrowserLayout.css';

export default function usePortraitBrowserLayout() {
  useLayoutEffect(() => {
    // The broadcast app controls its own phone/tablet orientation natively.
    if (window.ReactNativeWebView || !window.matchMedia?.('(pointer: coarse)').matches) return;

    const root = document.documentElement;
    const orientation = window.screen.orientation as ScreenOrientation & {
      lock?: (value: 'portrait-primary') => Promise<void>;
    };
    let disposed = false;
    let locking = false;
    let locked = false;

    const resize = () => {
      const width = window.screen.width || window.innerWidth;
      const height = window.screen.height || window.innerHeight;
      const legacyAngle = (window as Window & { orientation?: number }).orientation;
      // A keyboard can make the viewport wide without rotating the device.
      const landscape = orientation?.type
        ? orientation.type.startsWith('landscape')
        : typeof legacyAngle === 'number' ? Math.abs(legacyAngle) === 90 : width > height;
      const angle = orientation?.angle ?? legacyAngle ?? (landscape ? 90 : 0);
      const naturalLandscape = landscape === (Math.abs(angle % 180) === 0);
      const rotation = ((angle - (naturalLandscape ? 90 : 0)) % 360 + 360) % 360;
      const portraitWidth = landscape ? window.innerHeight : window.innerWidth;
      const portraitHeight = landscape ? window.innerWidth : window.innerHeight;
      root.style.setProperty('--browser-portrait-width', `${portraitWidth}px`);
      root.style.setProperty('--browser-portrait-height', `${portraitHeight}px`);
      root.style.setProperty('--browser-counter-rotation', `${-rotation}deg`);
      if (rotation) root.setAttribute('data-browser-rotation', String(rotation));
      else root.removeAttribute('data-browser-rotation');
    };

    const lock = async () => {
      if (!orientation?.lock || locking || document.hidden) return;
      locking = true;
      try {
        await orientation.lock('portrait-primary');
        locked = true;
        if (disposed) orientation.unlock();
      } catch {
        // Ordinary browser tabs may require fullscreen; keep the portrait layout instead.
      } finally {
        locking = false;
      }
    };
    const resume = () => { if (!document.hidden) { resize(); void lock(); } };
    let touch: { x: number; y: number; target: Element | null } | null = null;
    const startTouch = (event: TouchEvent) => {
      const point = event.touches[0];
      touch = event.touches.length === 1
        ? { x: point.clientX, y: point.clientY, target: event.target instanceof Element ? event.target : null }
        : null;
    };
    const endTouch = () => { touch = null; };
    const moveTouch = (event: TouchEvent) => {
      const rotation = Number(root.getAttribute('data-browser-rotation'));
      if (!touch || !rotation || event.touches.length !== 1) return;
      const point = event.touches[0];
      const dx = point.clientX - touch.x, dy = point.clientY - touch.y;
      const vertical = rotation === 90 ? dx : rotation === 270 ? -dx : -dy;
      const horizontal = rotation === 180 ? dx : dy;
      if (Math.abs(vertical) <= Math.abs(horizontal)) return;
      // Chrome does not map native scrolling to a counter-rotated scroll container.
      for (let element = touch.target; element; element = element?.parentElement ?? null) {
        if (!/auto|scroll/.test(getComputedStyle(element).overflowY) || element.scrollHeight <= element.clientHeight) continue;
        if (event.cancelable) event.preventDefault();
        element.scrollTop -= vertical;
        touch.x = point.clientX;
        touch.y = point.clientY;
        return;
      }
    };
    resize();
    void lock();
    window.addEventListener('resize', resize);
    window.addEventListener('orientationchange', resize);
    orientation?.addEventListener('change', resize);
    document.addEventListener('fullscreenchange', resume);
    document.addEventListener('visibilitychange', resume);
    document.addEventListener('touchstart', startTouch, { passive: true });
    document.addEventListener('touchmove', moveTouch, { passive: false });
    document.addEventListener('touchend', endTouch);
    document.addEventListener('touchcancel', endTouch);

    return () => {
      disposed = true;
      window.removeEventListener('resize', resize);
      window.removeEventListener('orientationchange', resize);
      orientation?.removeEventListener('change', resize);
      document.removeEventListener('fullscreenchange', resume);
      document.removeEventListener('visibilitychange', resume);
      document.removeEventListener('touchstart', startTouch);
      document.removeEventListener('touchmove', moveTouch);
      document.removeEventListener('touchend', endTouch);
      document.removeEventListener('touchcancel', endTouch);
      if (locked) orientation.unlock();
      root.removeAttribute('data-browser-rotation');
      root.style.removeProperty('--browser-portrait-width');
      root.style.removeProperty('--browser-portrait-height');
      root.style.removeProperty('--browser-counter-rotation');
    };
  }, []);
}
